// Terrain material: painted texture layers blended per fragment from per-hex ground cover, so biome
// edges wander like paint instead of following hex outlines; then rock, cliff walls, glacier snow,
// wetlands and cavity occlusion; then the ordered surface-layer chain.
//
//   ground cover (hexdata.ground at 3 hex centres around a noise-warped point) ─► 8 weights
//     ─► top two ─► height blend with each layer's painted height (albedo alpha)
//     ─► painted grass / plains / desert / tundra / snow layers · procedural canopy for forests
//   ─► alpine steppe tone on high plateaus ─► marsh pools ─► beach / sea floor
//   ─► rock (mountains, steep slopes) ─► cliff walls (triplanar cliff layer, darker foot)
//   ─► glacier snow (hexdata.snow: snow line + patch share per hex, evaluated per fragment)
//   ─► cavity occlusion ─► brush strokes ─► surface layers ─► painted lighting
//   layer normal maps + micro height ─► bump ─► normalNode
//
// Texture coordinates repeat a whole number of times across WORLD_W, so the east-west wrap never
// shows a seam; a second sample rotated 37° at a lower frequency hides the tiling.

import {
  Fn, If, abs, attribute, cameraViewMatrix, cross, dFdx, dFdy, dot, float, fwidth, int, max, mix, normalViewGeometry, normalWorldGeometry,
  normalize, positionView, positionWorld, pow, select, sin, smoothstep, texture, vec2, vec3, vec4,
} from 'three/tsl';
import type Node from 'three/src/nodes/core/Node.js';
import { WORLD_W } from '../../core/grid.ts';
import type { HexData } from '../hexdata.ts';
import { PaintedMaterial, type PaintStyle } from '../lighting.ts';
import type { SurfaceChain, SurfaceCtx } from '../surface.ts';
import { rgb, screen, screenNode } from '../tsl/color.ts';
import { hexBary, hexFrag, wrapXNode, type HexFrag } from '../tsl/hex.ts';
import { crossNoise, strokes, worldCells, worldFbm, worldNoise } from '../tsl/noise.ts';
import { GROUND } from './heights.ts';
import { BIOME_COLORS, SURFACE_COLORS } from './palette.ts';
import { LAYER, type TerrainTextures } from './textures.ts';

type F = Node<'float'>;
type V2 = Node<'vec2'>;
type V3 = Node<'vec3'>;
type V4 = Node<'vec4'>;

export interface TerrainMaterialDeps {
  data: HexData;
  surface: SurfaceChain;
  style: PaintStyle;
  textures: TerrainTextures;
  /** Seconds since start. */
  time: F;
  /** Camera height above ground, hex radii. */
  camHeight: F;
}

const BASE_ROUGHNESS = 0.9;
const POOL_ROUGHNESS = 0.15;

// Detail frequencies (cells per world unit; a hex is ~1.7 units across).
const BLOTCH_FREQ = 0.9;
const PATCH_FREQ = 2.2;
const GRAIN_FREQ = 9;
const STROKE_FREQ = 2.4;
const CELL_FREQ = 5.5;
const POOL_FREQ = 2.1;
const POOL_WARP_FREQ = 4.3; // pool outlines wander: the cells are read at a point pushed by this noise
const POOL_WARP_AMP = 0.16; // world units
const CRAG_FREQ = 4.5;
const STREAK_FREQ = 7;
const STREAK_STRETCH = 0.22; // gullies run downhill: vertical wavelength ≈ 4.5× the horizontal

// Texture tiling: whole repeats across the world (≈0.35 and ≈0.11 per world unit), the second
// rotated 37° (78, 59 ≈ 0.11 · W · (cos 37°, sin 37°)).
const REPEAT_A = 310;
const ROT_B = [78, 59] as const;
const SIDE_REPEAT = 520; // cliff walls, ≈0.6 per world unit
const SIDE_STRETCH = 1.6; // walls: taller texels read as vertical strata and gullies
const TEX_B_MIX = 0.45;

// Biome blending: ground cover is read at a point warped by fbm (amplitude in world units), then
// the two strongest layers are height-blended with the painted heights in the albedo alpha.
const WARP_AMP = 0.3;
const WARP_FREQ = 1.5;
const HEIGHT_GAIN = 0.5;
const BLEND_DEPTH = 0.2;

// World band: from SPREAD_HEIGHT[0] a hex shrinks toward a few pixels and per-hex ground cover would
// read as square tiles. Ground cover is then averaged over a ring of SPREAD_RING samples (radius in
// world units; hex spacing is 1.73) read at a point pushed by broad noise, so biome edges become
// smooth painted contours. Full effect from SPREAD_HEIGHT[1] (camera heights, hex radii).
const SPREAD_HEIGHT = [90, 240] as const;
const SPREAD_RING = 6;
const SPREAD_RADIUS = 2.4;
const SPREAD_WARP_FREQ = 0.4;
const SPREAD_WARP_AMP = 1.3;
const SPREAD_MIN = 0.001;
const SPREAD_MIP_BIAS = 3; // extra mip levels at full spread: painted flecks repeat as a lattice from afar

// Alpine steppe: high plateaus lose the lowland green (metres of mean elevation). High desert
// (Puna, Iranian plateau) keeps more of its own colour than grass does.
const ALPINE_M = [2200, 4200] as const;
const ALPINE = '#a2977c';
const ALPINE_SHARE = 0.85;
const ALPINE_DESERT = 0.6;

const BEACH_SHARE = 0.85; // how far a beach replaces the ground at the water

// Glacier snow: transition half-width (world units), noise, slope mask on the ground normal's y.
const SNOW_EDGE = 0.06;
const SNOW_NOISE = 0.16;
const SNOW_NOISE_FREQ = 2.4;
const SNOW_UP = [0.55, 0.85] as const;
const SNOW_STEEP_MIN = 0.3;
const PATCH_NOISE_FREQ = 3.2;
const SNOW_LIT = '#d0dbe6';
const SNOW_SHADE = '#9fb4c8';

// Land ice (ice-sheet hexes paint GROUND.SNOW): flat pale blue-white, a blue-grey coast line and ice
// cliffs instead of sand and sandstone, and blue-grey crevasse strokes. The strokes are decoration
// (they mark no data): denser toward the coast, drawn only while a cell spans CREVASSE.fadeCell
// pixels or more.
const ICE_LAND = { lit: '#dbe8f3', shade: '#b9cfe2', crevasse: '#7f9fba', rim: '#7d9db6' } as const;
const ICE_LAND_SHARE = 1; // how far the ice tone replaces the painted layer
const ICE_FLAT = 0.75; // how far land ice's shading normal leans to straight up: the sheet lights evenly
const ICE_SOFT_KEEP = 0.3; // share of the cavity darkening and brush strokes land ice keeps (soft mottling reads as cloud)
const ICE_WALL = [0.5, 0.8] as const; // ground weight that turns a cliff wall into ice
const ICE_HASH = 7.13; // spreads a cell id over [0, 1) again for the stroke draw
const CREVASSE = { ink: 0.9, freq: 1.7, stretch: 0.3, px: 1.3, share: 0.45, inland: 0.3, patchFreq: 1.3, fadeCell: [4, 8] as const } as const;

const ROCK_TINT = 0.3; // how much rock takes the colour of the ground around it
// Rock on dry ground (desert, plains, savanna) stays the ground's own tone: a dark grey hill on pale
// sand reads as a burn scar, which is data the game does not have (truth rule 10).
const ARID_ROCK_TINT = 0.75; // share of the rock's colour taken from the ground
const ARID_ROCK_BAND = 0.15; // rock luminance stays within this share of the ground's
const LUMA_WEIGHTS = [0.2126, 0.7152, 0.0722] as const;
const LUMA_MIN = 1e-3;
const STROKE_DARK = 0.9;
const STROKE_LIGHT = 1.07;
const CAVITY_DARKEN = 0.25;
const WALL_FOOT = 0.4; // share of a cliff wall's height that darkens toward its foot
const WALL_FOOT_DARK = 0.55;
const WALL_MIN = 0.12; // cliff walls never go darker than this linear albedo
const NORMAL_STRENGTH = 0.35;
const TEX_BUMP = 0.003; // world units of micro height from the painted height maps
const BUMP_GAIN = 1.0;
const BUMP_MAX_TILT = 0.9; // tangent of the largest bump tilt
const SEABED_SHALLOW_Y = -0.04;
const SEABED_DEEP_Y = -0.4;
const TRIPLANAR_SHARPNESS = 4;

// Canopy colours for forest channels (seen from mid and world zoom; trees stand on top up close).
const CANOPY = {
  broadleaf: { gap: SURFACE_COLORS.forestGap, crown: BIOME_COLORS.forest[0], light: BIOME_COLORS.forest[1] },
  conifer: { gap: '#16301f', crown: '#2b6440', light: '#3f7a4c' },
  jungle: { gap: SURFACE_COLORS.jungleGap, crown: BIOME_COLORS.jungle[0], light: BIOME_COLORS.jungle[1] },
} as const;

// Grass and plains macro patches (share of a tint toward sunlit green / dry grass).
const PATCH_SUN = 0.3;
const PATCH_DRY = 0.28;

// Marsh pools: cell radius range (cell units), edge softness, and the multiplier that turns a cell id
// into an independent size draw.
const POOL_SIZE = [0.16, 0.34] as const;
const POOL_SOFT = 0.05;
const POOL_ID_SPREAD = 13.7;

// Pale flecks (lichen blobs on tundra, pebbles on plains) read as noise from afar: linear colour
// above the cap keeps only FLECK_KEEP of its excess (caps are the 90th percentile of each layer).
const FLECK_KEEP = 0.25;
const TUNDRA_CAP = [0.31, 0.32, 0.2] as const;
const PLAINS_CAP = [0.5, 0.46, 0.14] as const;

// Desert layer: share pulled toward its mean colour (linear), so dune ripples stay a texture.
const DESERT_SOFTEN = 0.4;
const DESERT_MEAN = [0.62, 0.45, 0.24] as const;

// Forest floor under the tree models (close band), as a multiplier on the ground layer.
const FOREST_FLOOR = '#6f8a5a';
const FOREST_FLOOR_GAIN = 1.1;
const FLOOR_HEIGHT = [70, 35] as const; // canopy pattern above the first height, floor below the second

// Texture layer per ground channel (GROUND order); forest channels use a ground layer for height.
const LAYER_OF: readonly number[] = [LAYER.GRASS, LAYER.PLAINS, LAYER.DESERT, LAYER.TUNDRA, LAYER.SNOW, LAYER.GRASS, LAYER.TUNDRA, LAYER.GRASS];
const FOREST_FIRST = GROUND.BROADLEAF;

interface Fields {
  xz: V2;
  /** World units per pixel. */
  px: F;
  blotch: F;
  patch: F;
  grain: F;
  cells: V3;
  pools: V3;
}

/** 1 while a detail of wavelength `lambda` spans several pixels, 0 once it would alias. */
function detail(f: Fields, lambda: number): F {
  return smoothstep(lambda * 0.5, lambda * 0.18, f.px);
}

/** Stricter fade for bump heights: screen derivatives need ~8+ pixels per wavelength. */
function bumpFade(f: Fields, lambda: number): F {
  return smoothstep(lambda * 0.25, lambda * 0.12, f.px);
}

/** Pick among constants by an integer-valued float node. */
function pickConst(i: F, values: readonly number[]): F {
  let out: F = float(values[values.length - 1]);
  for (let k = values.length - 2; k >= 0; k--) {
    out = select(i.lessThan(k + 0.5), float(values[k]), out);
  }
  return out;
}

/** Pick among vec3 nodes by an integer-valued float node. */
function pickVec(i: F, values: readonly V3[]): V3 {
  let out: V3 = values[values.length - 1];
  for (let k = values.length - 2; k >= 0; k--) {
    out = select(i.lessThan(k + 0.5), values[k], out);
  }
  return out;
}

/** Tree canopy from Worley cells: crowns bulge, gaps between them are dark. */
function canopy(f: Fields, colors: { gap: string; crown: string; light: string }, crownSize: number): { color: V3; height: F } {
  const crown = smoothstep(crownSize, 0.0, f.cells.x);
  const shade = mix(screen(colors.crown), screen(colors.light), f.cells.z.mul(0.7).add(crown.mul(0.3)));
  const fade = detail(f, 1 / CELL_FREQ);
  const c = mix(mix(screen(colors.crown), screen(colors.gap), 0.35), mix(screen(colors.gap), shade, crown), fade);
  return { color: c, height: crown.mul(0.03).mul(bumpFade(f, 1 / CELL_FREQ)) };
}

interface Marsh {
  color: V3;
  pool: F;
}

/** Wetland: muddy olive ground and irregular still pools that mirror the sky. */
function marsh(f: Fields, albedo: V3, w: F, marshTex: V3): Marsh {
  const strength = smoothstep(0.08, 0.6, w);
  // Pools: low-frequency Worley cells read at a warped point, each cell its own size, with ragged
  // edges; neighbours merge into lagoons.
  const ragged = f.pools.x.add(f.patch.sub(0.5).mul(0.22)).add(f.grain.sub(0.5).mul(0.08));
  const edge = mix(POOL_SIZE[0], POOL_SIZE[1], f.pools.z.mul(POOL_ID_SPREAD).fract());
  const pool = smoothstep(edge.add(POOL_SOFT), edge.sub(POOL_SOFT), ragged).mul(smoothstep(0.35, 0.5, f.pools.z)).mul(detail(f, 1 / POOL_FREQ)).mul(strength);
  const ground = mix(marshTex, screen(SURFACE_COLORS.marshMud), f.patch.mul(0.35));
  const water = mix(screen(SURFACE_COLORS.marshWater), screen(SURFACE_COLORS.marshSky), f.grain.mul(0.35));
  const color = mix(mix(albedo, ground, strength.mul(0.8)), water, pool);
  return { color, pool };
}

/** Triplanar blend weights from a world normal (sharpened). */
function triWeights(n: V3): V3 {
  const w = pow(abs(n), vec3(TRIPLANAR_SHARPNESS));
  return w.div(w.x.add(w.y).add(w.z).max(1e-4));
}

/**
 * Triplanar value noise: top faces use XZ, side faces XY and ZY. `stretch` < 1 elongates the
 * side projections vertically (downhill streaks). X stays on the periodic lattice.
 */
function triNoise(world: V3, xz: V2, w: V3, freq: number, stretch: number): F {
  const top = crossNoise(xz, freq);
  const front = worldNoise(vec2(xz.x, world.y.mul(stretch).add(37.1)), freq);
  const side = worldNoise(vec2(world.z, world.y.mul(stretch).add(71.3)), freq);
  return side.mul(w.x).add(top.mul(w.y)).add(front.mul(w.z));
}

/** Mikkelsen surface-gradient bump: perturb a view-space normal by a height field's screen slope. */
function bumpNormal(height: F, base: V3): V3 {
  const n = normalViewGeometry;
  const dpdx = dFdx(positionView);
  const dpdy = dFdy(positionView);
  const r1 = cross(dpdy, n);
  const r2 = cross(n, dpdx);
  const det = dot(dpdx, r1);
  const dhdx = dFdx(height).mul(BUMP_GAIN);
  const dhdy = dFdy(height).mul(BUMP_GAIN);
  const grad = r1.mul(dhdx).add(r2.mul(dhdy)).mul(det.sign());

  // Clamp the tilt so steep noise can never flip a normal away from the surface (dark specks).
  const limit = det.abs().mul(BUMP_MAX_TILT);
  const len = grad.length().max(1e-12);
  const clamped = grad.mul(limit.div(len).min(1));
  return normalize(base.mul(det.abs().max(1e-12)).sub(clamped));
}

export function createTerrainMaterial(deps: TerrainMaterialDeps): PaintedMaterial {
  const material = new PaintedMaterial(deps.style, { roughness: BASE_ROUGHNESS, metalness: 0 });
  const build = (): void => {
    buildNodes(material, deps);
    material.needsUpdate = true;
  };
  build();
  deps.surface.onChange(build);
  return material;
}

/** Texture samplers over the two layer arrays, with the world-periodic UV sets. */
interface Layers {
  /** Planar XZ sample of albedo layer `layer` (two rotated samples mixed): linear rgb + height. */
  albedo(layer: F): V4;
  /** Planar XZ tangent-space normal of `layer` (x east, y south, z up), in -1..1. */
  normal(layer: F): V3;
  /** Triplanar side sample (walls) of `layer`. */
  side(layer: number, nGeo: V3): V4;
}

function layers(textures: TerrainTextures, world: V3, mixB: F, bias: F): Layers {
  const uvA = vec2(world.x, world.z).mul(REPEAT_A / WORLD_W);
  const uvB = vec2(world.x.mul(ROT_B[0]).add(world.z.mul(ROT_B[1])), world.x.mul(-ROT_B[1]).add(world.z.mul(ROT_B[0]))).div(WORLD_W);
  return {
    albedo(layer) {
      const index = int(layer).toVar();
      const a = texture(textures.albedo, uvA).depth(index).bias(bias);
      const b = texture(textures.albedo, uvB).depth(index).bias(bias);
      return mix(a, b, mixB);
    },
    normal(layer) {
      return texture(textures.normal, uvA).depth(int(layer).toVar()).xyz.mul(2).sub(1);
    },
    side(layer, nGeo) {
      const w = triWeights(nGeo);
      const k = SIDE_REPEAT / WORLD_W;
      const front = texture(textures.albedo, vec2(world.x.mul(k), world.y.mul(k * SIDE_STRETCH))).depth(int(layer));
      const side = texture(textures.albedo, vec2(world.z.mul(k), world.y.mul(k * SIDE_STRETCH))).depth(int(layer));
      const top = texture(textures.albedo, uvA).depth(int(layer));
      return side.mul(w.x).add(top.mul(w.y)).add(front.mul(w.z)).div(w.x.add(w.y).add(w.z).max(1e-4));
    },
  };
}

/** Ground cover weights at a fragment, normalised over land (water centres carry none). */
function groundWeights(data: HexData, frag: HexFrag): { w: F[]; land: F } {
  const g = data.groundSmooth(frag);
  const raw = [g.a.x, g.a.y, g.a.z, g.a.w, g.b.x, g.b.y, g.b.z, g.b.w];
  let sum: F = float(0);
  for (const v of raw) {
    sum = sum.add(v);
  }
  const land = sum.toVar('groundLand');
  const inv = float(1).div(land.max(1e-3));
  return { w: raw.map((v) => v.mul(inv)), land };
}

/**
 * Ground weights for the world band: `near` (one sample at `p`) mixed toward the average of a ring of
 * samples around a noise-pushed `p`, by `spread` (0 = near). Raw weights are averaged before they are
 * normalised over land, so a coast does not dilute the land beside it.
 */
function spreadWeights(data: HexData, p: V2, xz: V2, near: F[], spread: F): F[] {
  const out = near.map((v, k) => v.toVar(`gSpread${k}`));
  If(spread.greaterThan(SPREAD_MIN), () => {
    const push = vec2(worldFbm(xz.add(vec2(0, 29.3)), SPREAD_WARP_FREQ), worldFbm(xz.add(vec2(0, 63.7)), SPREAD_WARP_FREQ)).sub(0.5).mul(SPREAD_WARP_AMP * 2);
    const centre = p.add(push.mul(spread));
    const radius = spread.mul(SPREAD_RADIUS);
    let sum: F[] = [];
    for (let k = 0; k <= SPREAD_RING; k++) {
      const angle = ((k - 1) / SPREAD_RING) * 2 * Math.PI;
      const at = k === 0 ? centre : centre.add(vec2(Math.cos(angle), Math.sin(angle)).mul(radius));
      const g = data.groundSmooth(hexFrag(at, `gRing${k}`));
      const raw = [g.a.x, g.a.y, g.a.z, g.a.w, g.b.x, g.b.y, g.b.z, g.b.w];
      sum = sum.length === 0 ? raw : sum.map((v, c) => v.add(raw[c]));
    }
    const land = sum.reduce((a, v) => a.add(v), float(0)).max(1e-3);
    out.forEach((v, c) => v.assign(mix(v, sum[c].div(land), spread)));
  });
  return out;
}

/** Index and value of the largest weight, optionally skipping index `skip`. */
function strongest(w: F[], skip?: F): { i: F; v: F } {
  let bestI: F = float(-1);
  let bestV: F = float(-1);
  w.forEach((v, k) => {
    const eligible = skip ? v.greaterThan(bestV).and(skip.notEqual(k)) : v.greaterThan(bestV);
    bestI = select(eligible, float(k), bestI);
    bestV = select(eligible, v, bestV);
  });
  return { i: bestI, v: bestV.max(0) };
}

function buildNodes(material: PaintedMaterial, deps: TerrainMaterialDeps): void {
  const emissiveOut = vec3(0).toVar('terrainEmissive');
  const roughOut = float(BASE_ROUGHNESS).toVar('terrainRough');
  const normalOut = vec3(0, 0, 1).toVar('terrainNormal');

  material.colorNode = Fn(() => {
    const world = positionWorld;
    const xz = vec2(wrapXNode(world.x), world.z);
    const hex = hexFrag(world.xz);
    const bB = attribute('biomeB', 'vec4') as V4;
    const aux = attribute('aux', 'vec4') as V4;
    const shape = attribute('shape', 'vec4') as V4;
    const nGeo = normalWorldGeometry;
    const px = fwidth(world.xz).length().toVar('terrainPx');

    const poolWarp = vec2(worldNoise(xz.add(vec2(0, 83.1)), POOL_WARP_FREQ), worldNoise(xz.add(vec2(0, 17.9)), POOL_WARP_FREQ)).sub(0.5).mul(POOL_WARP_AMP * 2);
    const f: Fields = {
      xz,
      px,
      blotch: worldFbm(xz, BLOTCH_FREQ).toVar('tBlotch'),
      patch: worldNoise(xz.add(vec2(0, 31.7)), PATCH_FREQ).toVar('tPatch'),
      grain: worldNoise(xz, GRAIN_FREQ).toVar('tGrain'),
      cells: worldCells(xz, CELL_FREQ).toVar('tCells'),
      pools: worldCells(xz.add(vec2(0, 51.3)).add(poolWarp), POOL_FREQ).toVar('tPools'),
    };
    const spread = smoothstep(SPREAD_HEIGHT[0], SPREAD_HEIGHT[1], deps.camHeight);
    const tex = layers(deps.textures, world, smoothstep(0.3, 0.7, f.blotch).mul(TEX_B_MIX).add(0.15), spread.mul(SPREAD_MIP_BIAS));

    // Ground cover at a warped point: biome edges wander across hex outlines.
    const warp = vec2(worldFbm(xz.add(vec2(0, 11.3)), WARP_FREQ), worldFbm(xz.add(vec2(0, 47.9)), WARP_FREQ)).sub(0.5).mul(WARP_AMP * 2);
    const gHex = hexFrag(world.xz.add(warp), 'gnd');
    const w = spreadWeights(deps.data, world.xz.add(warp), xz, groundWeights(deps.data, gHex).w, spread);
    const first = strongest(w);
    const i1 = first.i.toVar('gI1');
    const second = strongest(w, i1);
    const i2 = second.i.max(0).toVar('gI2');

    // The two strongest layers, height-blended.
    const s1 = tex.albedo(pickConst(i1, LAYER_OF)).toVar('gTex1');
    const s2 = tex.albedo(pickConst(i2, LAYER_OF)).toVar('gTex2');
    const a1 = s1.w.mul(HEIGHT_GAIN).add(first.v);
    const a2 = s2.w.mul(HEIGHT_GAIN).add(second.v);
    const cut = max(a1, a2).sub(BLEND_DEPTH);
    const b1 = max(a1.sub(cut), 0);
    const b2 = max(a2.sub(cut), 0);
    const t2 = b2.div(b1.add(b2).max(1e-4)).toVar('gT2');

    // Forest channels: a canopy pattern from afar; up close, where tree models stand, a dark
    // forest floor from the channel's ground layer instead.
    const forests = [canopy(f, CANOPY.broadleaf, 0.62), canopy(f, CANOPY.conifer, 0.5), canopy(f, CANOPY.jungle, 0.8)];
    const floorShare = smoothstep(FLOOR_HEIGHT[0], FLOOR_HEIGHT[1], deps.camHeight);
    const channelColor = (i: F, sample: V4): V3 => {
      // Desert: soften the painted dunes toward their mean so the pattern does not dominate.
      const soft = select(i.equal(GROUND.DESERT), float(DESERT_SOFTEN), float(0));
      const cap = select(i.equal(GROUND.TUNDRA), vec3(...TUNDRA_CAP), select(i.equal(GROUND.PLAINS), vec3(...PLAINS_CAP), vec3(1)));
      const flat = sample.xyz.sub(sample.xyz.sub(cap).max(0).mul(1 - FLECK_KEEP));
      const rgbTex = mix(flat, vec3(DESERT_MEAN[0], DESERT_MEAN[1], DESERT_MEAN[2]), soft);
      let painted = screenNode(rgbTex).mul(f.blotch.sub(0.5).mul(0.16).add(1));
      // Grass and plains: broad sunlit and dry patches, so open country is never one flat green.
      const grassy = select(i.lessThan(GROUND.DESERT - 0.5), float(1), float(0));
      painted = mix(painted, painted.mul(screen(SURFACE_COLORS.grassSun)).mul(2.2), smoothstep(0.55, 0.9, f.patch).mul(PATCH_SUN).mul(grassy));
      painted = mix(painted, painted.mul(screen(SURFACE_COLORS.dryGrass)).mul(2.2), smoothstep(0.6, 0.85, f.blotch).mul(PATCH_DRY).mul(grassy));
      const forest = pickVec(i.sub(FOREST_FIRST).max(0), forests.map((c) => c.color));
      const floor = painted.mul(screen(FOREST_FLOOR)).mul(FOREST_FLOOR_GAIN);
      return select(i.greaterThanEqual(FOREST_FIRST), mix(forest, floor, floorShare), painted);
    };
    const forestHeight = forests[0].height;
    const albedo = mix(channelColor(i1, s1), channelColor(i2, s2), t2).toVar('terrainAlbedo');
    const height = mix(s1.w, s2.w, t2).mul(TEX_BUMP).mul(bumpFade(f, 1 / PATCH_FREQ)).toVar('terrainMicro');
    const isForest = select(i1.greaterThanEqual(FOREST_FIRST), float(1), float(0)).mul(t2.oneMinus())
      .add(select(i2.greaterThanEqual(FOREST_FIRST), float(1), float(0)).mul(t2));
    height.addAssign(forestHeight.mul(isForest));

    // Painted layer normals (tangent frame: east, south, up).
    const n1 = tex.normal(pickConst(i1, LAYER_OF));
    const n2 = tex.normal(pickConst(i2, LAYER_OF));
    const nTex = mix(n1, n2, t2).mul(vec3(NORMAL_STRENGTH, NORMAL_STRENGTH, 1));
    const nFade = bumpFade(f, 1 / PATCH_FREQ).mul(isForest.oneMinus());

    // High plateaus: grass, plains and desert fade to a dry alpine steppe tone.
    const elev = deps.data.reliefSmooth(hex).x;
    const dry = w[GROUND.GRASS].add(w[GROUND.PLAINS]).add(w[GROUND.DESERT].mul(ALPINE_DESERT)).min(1);
    albedo.assign(mix(albedo, screen(ALPINE), smoothstep(ALPINE_M[0], ALPINE_M[1], elev).mul(dry).mul(ALPINE_SHARE)));

    // Marsh pools (vertex marsh share).
    const marshTex = screenNode(tex.albedo(float(LAYER.MARSH)).xyz);
    const wet = marsh(f, albedo, shape.z, marshTex);
    albedo.assign(wet.color);
    height.assign(mix(height, float(-0.004), wet.pool));

    // Beaches: land blended toward the sea floor weight near coasts and lakeshores.
    const beachTex = screenNode(tex.albedo(float(LAYER.BEACH)).xyz);
    const shore = smoothstep(0.12, 0.45, bB.w);
    const beach = shore.mul(smoothstep(-0.02, 0.05, world.y));
    const shoreTone = mix(mix(screen(SURFACE_COLORS.beach), beachTex, 0.6), rgb(ICE_LAND.rim), w[GROUND.SNOW]);
    albedo.assign(mix(albedo, shoreTone, beach.mul(BEACH_SHARE)));

    // Sea floor darkens with depth under the water plane.
    const deep = smoothstep(SEABED_SHALLOW_Y, SEABED_DEEP_Y, world.y);
    albedo.assign(mix(albedo, screen(SURFACE_COLORS.seabedDeep), deep.mul(bB.w)));

    // Rock: mountains (mostly bare, greener toward their feet) and steep slopes. Triplanar noise
    // so faces keep their detail instead of smearing a top-down projection.
    const tri = triWeights(nGeo);
    const slope = nGeo.y.oneMinus();
    const peak = shape.x;
    const crag = triNoise(world, xz, tri, CRAG_FREQ, 1).toVar('tCrag');
    const streak = triNoise(world, xz, tri, STREAK_FREQ, STREAK_STRETCH).toVar('tStreak');
    const strata = sin(world.y.mul(55).add(crag.mul(5))).mul(0.5).add(0.5).mul(smoothstep(0.5, 0.8, slope));
    const greyRock = mix(mix(screen(SURFACE_COLORS.rockDark), screen(SURFACE_COLORS.rock), crag), screen(SURFACE_COLORS.rockLight), strata.mul(0.3));
    const warmRock = mix(greyRock, screen(SURFACE_COLORS.rockWarm), smoothstep(0.35, 0.75, f.blotch).mul(0.45));
    const gullies = mix(float(1), streak.mul(0.35).add(0.75), smoothstep(0.25, 0.6, slope));
    const arid = w[GROUND.DESERT].add(w[GROUND.PLAINS]).min(1);
    const tint = mix(float(ROCK_TINT), float(ARID_ROCK_TINT), arid);
    const looseRock = mix(warmRock, warmRock.mul(albedo).mul(2.2), tint).mul(gullies);
    const luma = vec3(...LUMA_WEIGHTS);
    const groundLuma = dot(albedo, luma);
    const rockLuma = max(dot(looseRock, luma), float(LUMA_MIN));
    const heldLuma = rockLuma.max(groundLuma.mul(1 - ARID_ROCK_BAND)).min(groundLuma.mul(1 + ARID_ROCK_BAND));
    const rockTone = mix(looseRock, looseRock.mul(heldLuma.div(rockLuma)), arid);
    // Inside a range (every contributor a mountain, aux.y ≈ 1) it is all rock; at the rim of a
    // range the high and steep parts stay rock and the feet green up.
    const inner = smoothstep(0.6, 0.95, aux.y.add(crag.sub(0.5).mul(0.3)));
    const feet = smoothstep(0.0, 0.35, peak.add(slope.mul(1.5)).add(crag.sub(0.5).mul(0.3)));
    const mountainRock = aux.y.min(1).mul(inner.max(mix(float(0.35), float(1), feet)));
    const steep = smoothstep(0.45, 0.72, slope);
    const rock = mountainRock.max(steep).min(1).toVar('terrainRock');
    albedo.assign(mix(albedo, rockTone, rock.mul(aux.x.oneMinus())));
    height.assign(mix(height, crag.mul(0.035).mul(bumpFade(f, 1 / CRAG_FREQ)), rock));

    // Cliff walls: the painted cliff layer projected on the wall, vertical gullies, darker foot.
    // A wall on a mountain (aux.y ≈ 1) is bare rock like the slopes around it, not banded sandstone.
    const cliffTex = screenNode(tex.side(LAYER.CLIFF, nGeo).xyz);
    const foot = mix(float(WALL_FOOT_DARK), float(1), smoothstep(0, WALL_FOOT, aux.z));
    const sandstone = cliffTex.mul(streak.mul(0.3).add(0.85));
    const iceWall = mix(rgb(ICE_LAND.rim), rgb(ICE_LAND.shade), streak);
    const wallTone = mix(mix(sandstone, rockTone, aux.y.min(1)), iceWall, smoothstep(ICE_WALL[0], ICE_WALL[1], w[GROUND.SNOW]));
    const cliff = max(wallTone.mul(foot), vec3(WALL_MIN));
    albedo.assign(mix(albedo, cliff, aux.x));

    // Glacier snow from data (per hex, blended between centres): caps above the snow line on
    // mountains, noise-edged patches covering the glacier share elsewhere.
    const bary = hexBary(hex);
    const sn = deps.data.snowAt(bary.cells[0]).mul(bary.w.x)
      .add(deps.data.snowAt(bary.cells[1]).mul(bary.w.y))
      .add(deps.data.snowAt(bary.cells[2]).mul(bary.w.z));
    const snowNoise = worldFbm(xz.add(vec2(0, 71.1)), SNOW_NOISE_FREQ);
    const up = smoothstep(SNOW_UP[0], SNOW_UP[1], nGeo.y);
    const level = world.y.sub(sn.x).add(snowNoise.sub(0.5).mul(SNOW_NOISE));
    const cap = smoothstep(-SNOW_EDGE, SNOW_EDGE, level).mul(max(up, float(SNOW_STEEP_MIN)));
    const patchNoise = worldFbm(xz.add(vec2(0, 83.3)), PATCH_NOISE_FREQ);
    const patches = smoothstep(patchNoise.sub(0.05), patchNoise.add(0.05), sn.y).mul(max(up, float(SNOW_STEEP_MIN)));
    const snow = max(cap, patches).mul(aux.x.oneMinus()).toVar('terrainSnow');
    const snowTone = mix(rgb(SNOW_SHADE), rgb(SNOW_LIT), up.mul(0.7).add(f.grain.mul(0.3)));
    albedo.assign(mix(albedo, snowTone, snow));
    height.addAssign(snow.mul(0.01));

    // Land ice: pale flat tone and crevasse strokes toward the coast.
    const land = smoothstep(0.5, 0.8, w[GROUND.SNOW]).mul(aux.x.oneMinus());
    const iceTone = mix(rgb(ICE_LAND.shade), rgb(ICE_LAND.lit), f.grain.mul(0.5).add(0.4));
    const slits = worldCells(vec2(xz.x, xz.y.mul(CREVASSE.stretch)).add(vec2(0, 29.3)), CREVASSE.freq);
    const slitPx = px.mul(CREVASSE.freq * CREVASSE.px);
    const slit = smoothstep(slitPx, slitPx.mul(0.5), slits.y.sub(slits.x))
      .mul(select(slits.z.mul(ICE_HASH).fract().greaterThan(1 - CREVASSE.share), float(1), float(0)))
      .mul(smoothstep(CREVASSE.fadeCell[0], CREVASSE.fadeCell[1], float(1).div(px.mul(CREVASSE.freq).max(1e-4))))
      .mul(mix(float(CREVASSE.inland), float(1), shore).mul(smoothstep(0.4, 0.6, worldNoise(xz.add(vec2(0, 5.7)), CREVASSE.patchFreq).add(shore.mul(0.3)))));
    albedo.assign(mix(albedo, iceTone, land.mul(ICE_LAND_SHARE).mul(beach.mul(BEACH_SHARE).oneMinus())));
    albedo.assign(mix(albedo, rgb(ICE_LAND.crevasse), slit.mul(land).mul(CREVASSE.ink)));

    // Cavity occlusion from the mesher: valleys, channels and the feet of slopes darken.
    albedo.mulAssign(float(1).sub(shape.y.mul(CAVITY_DARKEN).mul(mix(float(1), float(ICE_SOFT_KEEP), land))));

    // Painterly brush strokes on everything.
    const brush = mix(float(STROKE_DARK), float(STROKE_LIGHT), strokes(xz, STROKE_FREQ));
    albedo.mulAssign(mix(brush, float(1), land.mul(1 - ICE_SOFT_KEEP)));

    // Normal: the painted layers' normal maps tilt the geometry normal, then the micro height bumps.
    const nLit = normalize(mix(nGeo, vec3(0, 1, 0), land.mul(ICE_FLAT)));
    const nWorld = normalize(nLit.add(vec3(nTex.x, 0, nTex.y).mul(nFade.mul(rock.oneMinus()).mul(snow.oneMinus()))));
    const nView = normalize(cameraViewMatrix.mul(vec4(nWorld, 0)).xyz);
    normalOut.assign(bumpNormal(height, nView));

    // Surface layers, in order.
    const base = albedo.toVar('terrainBase');
    const ctx: SurfaceCtx = {
      hex,
      world,
      xz,
      normal: nGeo,
      px,
      color: base,
      base,
      roughness: mix(float(BASE_ROUGHNESS), float(POOL_ROUGHNESS), max(wet.pool, 0)),
      data: deps.data,
      time: deps.time,
      camHeight: deps.camHeight,
    };
    let emissive: V3 = vec3(0);
    for (const layer of deps.surface.list()) {
      if (layer.color) {
        ctx.color = layer.color(ctx).toVar(`layer_${safeName(layer.name)}`);
      }
      if (layer.roughness) {
        ctx.roughness = layer.roughness(ctx).toVar(`rough_${safeName(layer.name)}`);
      }
      if (layer.emissive) {
        emissive = emissive.add(layer.emissive(ctx));
      }
    }
    emissiveOut.assign(emissive);
    roughOut.assign(ctx.roughness);
    return ctx.color;
  })();
  material.emissiveNode = emissiveOut;
  material.roughnessNode = roughOut;
  material.normalNode = normalOut;
}

function safeName(name: string): string {
  return name.replace(/[^A-Za-z0-9]/g, '_');
}
